/**
 * PDF ダウンロード型資料 新規作成ダイアログ。
 *   1. タイトル + PDF ファイルをドラッグ&ドロップ or 選択
 *   2. リンク有効期限を設定 (デフォルト 168h、無期限可)
 *   3. 「作成」で Firestore レコード → Storage アップロード → registerPdfDocumentFile
 *
 * 完了後は viewer ページに遷移。
 */
import { FileUp, Loader2, Upload, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import {
  useCreatePdfDocument,
  useDeleteDocument,
  useUploadPdfDocumentFile,
} from '@/hooks/useDocuments';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (id: string) => void;
}

const MAX_SIZE_MB = 50;

export function PdfDocumentCreateDialog({ open, onOpenChange, onCreated }: Props) {
  const create = useCreatePdfDocument();
  const upload = useUploadPdfDocumentFile();
  const deleteDoc = useDeleteDocument();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [validityMode, setValidityMode] = useState<'preset' | 'unlimited'>('preset');
  const [validityHours, setValidityHours] = useState<number>(168);
  const [progress, setProgress] = useState<number>(0);
  const [phase, setPhase] = useState<'idle' | 'creating' | 'uploading' | 'registering'>(
    'idle',
  );

  const reset = () => {
    setTitle('');
    setFile(null);
    setDragOver(false);
    setValidityMode('preset');
    setValidityHours(168);
    setProgress(0);
    setPhase('idle');
  };

  const handleFile = (f: File | null | undefined) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) {
      toast.error('PDF ファイルのみアップロード可能です');
      return;
    }
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`ファイルサイズは ${MAX_SIZE_MB}MB 以下にしてください`);
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.pdf$/i, ''));
  };

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  const onSubmit = async () => {
    if (!title.trim()) {
      toast.error('タイトルを入力してください');
      return;
    }
    if (!file) {
      toast.error('PDF ファイルを選択してください');
      return;
    }
    let createdId: string | null = null;
    try {
      setPhase('creating');
      const tokenValidity =
        validityMode === 'unlimited' ? null : Math.max(1, Math.floor(validityHours));
      const created = await create.mutateAsync({
        title: title.trim(),
        tokenValidityHours: tokenValidity,
      });
      createdId = created.id;

      setPhase('uploading');
      setProgress(0);
      await upload.mutateAsync({
        documentId: created.id,
        file,
        onProgress: (pct) => setProgress(pct),
      });

      setPhase('registering');
      toast.success('PDF 資料を作成しました');
      onCreated(created.id);
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(`作成失敗: ${e instanceof Error ? e.message : String(e)}`);
      setPhase('idle');
      // アップロード失敗時はオーファン Firestore レコードを掃除
      if (createdId) {
        try {
          await deleteDoc.mutateAsync(createdId);
        } catch {
          // ベストエフォート — 失敗しても無視
        }
      }
    }
  };

  const busy = phase !== 'idle';

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (busy) return; // アップロード中は閉じさせない
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>PDF 資料を新規作成</DialogTitle>
          <DialogDescription>
            PDF をアップロードし、ダウンロードリンクを生成できる資料を作成します。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* タイトル */}
          <div className="space-y-1.5">
            <label htmlFor="pdf-title" className="text-[12px] font-medium">
              資料タイトル <span className="text-red-500">*</span>
            </label>
            <Input
              id="pdf-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="例: BioWell 製品紹介資料 2026"
              disabled={busy}
            />
          </div>

          {/* ファイルドロップゾーン */}
          <div className="space-y-1.5">
            <label className="text-[12px] font-medium">PDF ファイル <span className="text-red-500">*</span></label>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                if (!busy) setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => !busy && fileInputRef.current?.click()}
              className={`relative cursor-pointer rounded-md border-2 border-dashed p-6 text-center transition-colors ${
                dragOver
                  ? 'border-[var(--accent)] bg-[var(--accent)]/5'
                  : 'border-[var(--border)] hover:border-[var(--accent)]/50'
              } ${busy ? 'pointer-events-none opacity-60' : ''}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                disabled={busy}
              />
              {file ? (
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileUp className="size-5 text-[var(--accent)] shrink-0" />
                    <div className="text-left min-w-0">
                      <div className="text-sm font-medium truncate">{file.name}</div>
                      <div className="text-[11px] text-[var(--text-muted)]">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                  </div>
                  {!busy && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                      className="p-1 rounded hover:bg-[var(--bg-hover)]"
                      aria-label="ファイルをクリア"
                    >
                      <X className="size-4" />
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-[var(--text-muted)]">
                  <Upload className="size-8" />
                  <div className="text-[13px]">
                    クリックしてファイルを選択、またはここにドラッグ&ドロップ
                  </div>
                  <div className="text-[11px]">PDF / 最大 {MAX_SIZE_MB}MB</div>
                </div>
              )}
            </div>
          </div>

          {/* リンク有効期限 */}
          <div className="space-y-1.5">
            <label className="text-[12px] font-medium">ダウンロードリンクの既定有効期限</label>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-1 text-[12px]">
                <input
                  type="radio"
                  name="validity"
                  value="preset"
                  checked={validityMode === 'preset'}
                  onChange={() => setValidityMode('preset')}
                  disabled={busy}
                />
                時間指定
              </label>
              <label className="inline-flex items-center gap-1 text-[12px]">
                <input
                  type="radio"
                  name="validity"
                  value="unlimited"
                  checked={validityMode === 'unlimited'}
                  onChange={() => setValidityMode('unlimited')}
                  disabled={busy}
                />
                無期限
              </label>
              {validityMode === 'preset' && (
                <div className="flex items-center gap-1.5">
                  <Input
                    type="number"
                    min={1}
                    max={8760}
                    value={validityHours}
                    onChange={(e) => setValidityHours(Number(e.target.value) || 168)}
                    className="w-24"
                    disabled={busy}
                  />
                  <span className="text-[12px] text-[var(--text-muted)]">時間</span>
                </div>
              )}
            </div>
            <div className="text-[11px] text-[var(--text-muted)]">
              発行する各リンクの既定の有効時間です。リンクごとに個別設定もできます。
            </div>
          </div>

          {/* 進捗表示 */}
          {phase !== 'idle' && (
            <div className="space-y-1.5 rounded-md border border-[var(--border)] p-3">
              <div className="flex items-center gap-2 text-[12px]">
                <Loader2 className="size-4 animate-spin text-[var(--accent)]" />
                <span>
                  {phase === 'creating' && '資料を作成中…'}
                  {phase === 'uploading' && `PDF をアップロード中… ${progress}%`}
                  {phase === 'registering' && 'ファイルを登録中…'}
                </span>
              </div>
              {phase === 'uploading' && (
                <div className="h-1.5 w-full rounded-full bg-[var(--bg-elevated)] overflow-hidden">
                  <div
                    className="h-full bg-[var(--accent)] transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                onOpenChange(false);
                reset();
              }}
              disabled={busy}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => void onSubmit()}
              disabled={busy || !title.trim() || !file}
            >
              {busy ? '処理中…' : '作成してアップロード'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
